// frontend/src/components/MetricsViewer.js
import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, InputGroup, Form, Alert } from 'react-bootstrap';
import './MetricsViewer.css';

const MetricsViewer = () => {
  const [rawMetrics, setRawMetrics] = useState('');
  const [parsedMetrics, setParsedMetrics] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 获取原始指标
  const fetchRawMetrics = async () => {
    setLoading(true);
    try {
      const response = await fetch('/metrics');
      const text = await response.text();
      setRawMetrics(text);
      
      // 解析指标
      const parsed = parseMetrics(text);
      setParsedMetrics(parsed);
      setError(null);
    } catch (err) {
      setError('获取指标失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 解析 Prometheus 指标
  const parseMetrics = (text) => {
    const lines = text.split('\n');
    const metrics = [];
    let currentHelp = '';
    let currentType = '';

    lines.forEach(line => {
      line = line.trim();
      if (!line) return;

      if (line.startsWith('# HELP ')) {
        currentHelp = line.substring(7);
      } else if (line.startsWith('# TYPE ')) {
        currentType = line.substring(7);
      } else if (!line.startsWith('#')) {
        const match = line.match(/^([a-zA-Z_:][a-zA-Z0-9_:]*){?([^}]*)}?\s+([\d.e-]+)/);
        if (match) {
          const [, name, labels, value] = match;
          
          // 解析标签
          const labelObj = {};
          if (labels) {
            const labelPairs = labels.match(/(\w+)="([^"]*)"/g);
            if (labelPairs) {
              labelPairs.forEach(pair => {
                const [key, val] = pair.split('=');
                labelObj[key] = val.replace(/"/g, '');
              });
            }
          }

          metrics.push({
            name,
            labels: labelObj,
            value: parseFloat(value),
            help: currentHelp.includes(name) ? currentHelp.substring(currentHelp.indexOf(' ') + 1) : '',
            type: currentType.includes(name) ? currentType.split(' ')[1] : ''
          });
        }
      }
    });

    return metrics;
  };

  // 格式化数值
  const formatValue = (value, metricName) => {
    if (metricName.includes('bytes')) {
      // 内存单位转换
      if (value >= 1024 * 1024 * 1024) {
        return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
      } else if (value >= 1024 * 1024) {
        return `${(value / (1024 * 1024)).toFixed(2)} MB`;
      } else if (value >= 1024) {
        return `${(value / 1024).toFixed(2)} KB`;
      }
      return `${value} B`;
    } else if (metricName.includes('seconds')) {
      // 时间单位
      if (value >= 3600) {
        return `${(value / 3600).toFixed(2)} h`;
      } else if (value >= 60) {
        return `${(value / 60).toFixed(2)} min`;
      } else if (value >= 1) {
        return `${value.toFixed(2)} s`;
      } else {
        return `${(value * 1000).toFixed(2)} ms`;
      }
    } else if (metricName.includes('percentage') || metricName.includes('ratio')) {
      return `${(value * 100).toFixed(2)}%`;
    }
    
    return value.toFixed(4);
  };

  // 获取指标类型颜色
  const getTypeColor = (type) => {
    switch (type) {
      case 'counter': return 'primary';
      case 'gauge': return 'success';
      case 'histogram': return 'warning';
      case 'summary': return 'info';
      default: return 'secondary';
    }
  };

  // 过滤指标
  const filteredMetrics = parsedMetrics.filter(metric => 
    metric.name.toLowerCase().includes(filter.toLowerCase()) ||
    metric.help.toLowerCase().includes(filter.toLowerCase())
  );

  useEffect(() => {
    fetchRawMetrics();
    // 每30秒自动刷新
    const interval = setInterval(fetchRawMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="metrics-viewer">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">📊 系统指标详情</h5>
          <div className="d-flex gap-2">
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={fetchRawMetrics}
              disabled={loading}
            >
              {loading ? '刷新中...' : '🔄 刷新'}
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={() => window.open('/metrics', '_blank')}
            >
              📄 原始数据
            </Button>
          </div>
        </Card.Header>
        
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {/* 搜索过滤器 */}
          <InputGroup className="mb-3">
            <InputGroup.Text>🔍</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="搜索指标名称或描述..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </InputGroup>

          {/* 指标概览 */}
          <div className="mb-3">
            <small className="text-muted">
              共 {parsedMetrics.length} 个指标，显示 {filteredMetrics.length} 个
            </small>
          </div>

          {/* 指标表格 */}
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>指标名称</th>
                <th>类型</th>
                <th>当前值</th>
                <th>标签</th>
                <th>说明</th>
              </tr>
            </thead>
            <tbody>
              {filteredMetrics.map((metric, index) => (
                <tr key={index}>
                  <td>
                    <code className="small">{metric.name}</code>
                  </td>
                  <td>
                    <Badge bg={getTypeColor(metric.type)}>
                      {metric.type || 'unknown'}
                    </Badge>
                  </td>
                  <td>
                    <strong>{formatValue(metric.value, metric.name)}</strong>
                  </td>
                  <td>
                    {Object.keys(metric.labels).length > 0 ? (
                      <div>
                        {Object.entries(metric.labels).map(([key, value]) => (
                          <Badge key={key} bg="light" text="dark" className="me-1 mb-1">
                            {key}={value}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <small className="text-muted">{metric.help || '暂无说明'}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {filteredMetrics.length === 0 && !loading && (
            <div className="text-center py-4">
              <div className="text-muted">
                {filter ? '没有找到匹配的指标' : '暂无指标数据'}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default MetricsViewer;
